// ─── 日期时间 工具函数 ──────────────────────────────────────────────────────────

// 时间戳转换器：unix timestamp ↔ 日期字符串
export function timestampConvert(input) {
  const v = input.trim();
  // 纯数字 → 视为时间戳
  if (/^\d+$/.test(v)) {
    const ms = v.length <= 10 ? Number(v) * 1000 : Number(v);
    const d = new Date(ms);
    return {
      unix_s: Math.floor(ms / 1000),
      unix_ms: ms,
      iso: d.toISOString(),
      local: d.toLocaleString('zh-CN', { hour12: false }),
      utc: d.toUTCString(),
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    };
  }
  // 字符串 → 解析为时间戳
  const d = new Date(v);
  if (isNaN(d.getTime())) throw new Error('无法解析的时间格式');
  return {
    unix_s: Math.floor(d.getTime() / 1000),
    unix_ms: d.getTime(),
    iso: d.toISOString(),
    local: d.toLocaleString('zh-CN', { hour12: false }),
    utc: d.toUTCString(),
  };
}

// 时区转换
export function timezoneConvert(dateStr, fromTz, toTz) {
  // 使用 Intl API
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) throw new Error('无效的日期格式');
  const format = (tz) => new Intl.DateTimeFormat('zh-CN', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).format(d);
  return `原始时间 (${fromTz}):\n${format(fromTz)}\n\n转换后 (${toTz}):\n${format(toTz)}\n\nISO 8601:\n${d.toISOString()}`;
}

// 时间差计算
export function timeDiff(date1Str, date2Str) {
  const d1 = new Date(date1Str);
  const d2 = new Date(date2Str);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) throw new Error('无效的日期格式');
  const diffMs = Math.abs(d2 - d1);
  const totalSec = Math.floor(diffMs / 1000);
  const totalMin = Math.floor(totalSec / 60);
  const totalHour = Math.floor(totalMin / 60);
  const totalDay = Math.floor(totalHour / 24);
  const years = Math.floor(totalDay / 365);
  const months = Math.floor((totalDay % 365) / 30);
  const days = totalDay % 30;
  const hours = totalHour % 24;
  const minutes = totalMin % 60;
  const seconds = totalSec % 60;
  return {
    human: `${years > 0 ? years + '年 ' : ''}${months > 0 ? months + '个月 ' : ''}${days}天 ${hours}小时 ${minutes}分钟 ${seconds}秒`,
    totalDays: totalDay,
    totalHours: totalHour,
    totalMinutes: totalMin,
    totalSeconds: totalSec,
    totalMs: diffMs,
  };
}

// 随机时间生成
export function randomDateGenerate(startStr, endStr, count = 5) {
  const start = new Date(startStr).getTime();
  const end = new Date(endStr).getTime();
  if (isNaN(start) || isNaN(end)) throw new Error('无效的日期格式');
  const results = [];
  for (let i = 0; i < Math.min(count, 50); i++) {
    const r = new Date(start + Math.random() * (end - start));
    results.push(r.toISOString());
  }
  return results;
}

// UTC 时间转时间戳
export function utcToTimestamp(utcStr) {
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) throw new Error('无效的 UTC 时间格式');
  return {
    unix_s: Math.floor(d.getTime() / 1000),
    unix_ms: d.getTime(),
  };
}

// 秒 → 时:分:秒
export function secondsToHms(seconds) {
  const n = Number(seconds);
  if (isNaN(n)) throw new Error('请输入有效的秒数');
  const h = Math.floor(Math.abs(n) / 3600);
  const m = Math.floor((Math.abs(n) % 3600) / 60);
  const s = Math.floor(Math.abs(n) % 60);
  const sign = n < 0 ? '-' : '';
  return {
    formatted: `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
    hours: h, minutes: m, seconds: s,
  };
}

// 时:分:秒 → 秒
export function hmsToSeconds(hmsStr) {
  const parts = String(hmsStr).split(':').map(Number);
  if (parts.some(isNaN)) throw new Error('格式应为 HH:MM:SS');
  let total = 0;
  if (parts.length === 3) total = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) total = parts[0] * 60 + parts[1];
  else total = parts[0];
  return total;
}

// Cron 表达式解析
export function parseCron(expr) {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5 && parts.length !== 6) throw new Error('Cron 表达式应为 5 或 6 个字段');
  const fields5 = ['分钟', '小时', '日', '月', '星期'];
  const fields6 = ['秒', '分钟', '小时', '日', '月', '星期'];
  const fields = parts.length === 6 ? fields6 : fields5;

  function describeField(val, name) {
    if (val === '*') return `${name}: 每${name}`;
    if (val.startsWith('*/')) return `${name}: 每隔 ${val.slice(2)} ${name}`;
    if (val.includes('-')) {
      const [a, b] = val.split('-');
      return `${name}: ${a} 到 ${b}`;
    }
    if (val.includes(',')) return `${name}: ${val.split(',').join('、')}`;
    return `${name}: ${val}`;
  }

  const descriptions = parts.map((p, i) => describeField(p, fields[i]));
  // Next 5 execution times (approximate)
  const lines = [`表达式: ${expr}`, '', '字段解析:', ...descriptions];
  return lines.join('\n');
}

// 获取当前时间信息
export function getCurrentTimeInfo() {
  const now = new Date();
  return {
    iso: now.toISOString(),
    local: now.toLocaleString('zh-CN', { hour12: false }),
    unix_s: Math.floor(now.getTime() / 1000),
    unix_ms: now.getTime(),
    utc: now.toUTCString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekday: ['日', '一', '二', '三', '四', '五', '六'][now.getDay()],
  };
}

export const TIMEZONES = [
  'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore',
  'Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Europe/Moscow', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo',
  'Australia/Sydney', 'Pacific/Auckland', 'UTC'
];
